import {
  Avatar,
  FieldWrapper,
  FlexBox,
  InputSelectionList,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import type { SearchUser } from 'lib-shared-types-api';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { Waypoint } from 'react-waypoint';

import { searchApi } from 'apis/search';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { getUserDisplayName } from 'utils/user';

import styles from './contactSearchInput.module.scss';

export type ContactSearchInputProps = {
  /** When a contact is selected */
  onChange?: (user: SearchUser) => void;
};

/**
 * Contact Search Input is an input field that are used to search for a contact
 * to be merged with another contact.
 */
export const ContactSearchInput = ({
  onChange,
}: ContactSearchInputProps): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<SearchUser>();

  const {
    changeArg,
    items: searchUsers,
    asyncStatus,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchUser,
    itemsSelector: ({ data }) =>
      searchQuery.length > 0 ? data.results.hits : [],
    infinite: true,
  });

  useEffect(() => {
    changeArg(
      {
        searchTerm: searchQuery,
        filter: {
          facets: {
            user_type: ['contact'],
          },
        },
        offset: 0,
      },
      {
        debounceTime: 200,
      },
    );
  }, [changeArg, searchQuery]);

  // Override the automatic positioning calculated by the Dropdown component, which doesn't always
  // work as expected because the dropdown may be rendered inside a transformed container.
  // These styles makes sure the dropdown is rendered below the search input field.
  const dropdownPositionOverride: CSSProperties = {
    top: '100%',
    bottom: 'auto',
    margin: `${sizes.spacer[200]} 0 0 0`,
  };

  const handleOnChange = useCallback(
    (contact: SearchUser) => {
      setSelectedUser(contact);
      onChange?.(contact);
    },
    [onChange],
  );

  const handleToggleDropdown = useCallback(
    (isOpen: boolean) => {
      if (isOpen) return;

      if (!selectedUser) {
        setSearchQuery('');
      }
    },
    [selectedUser],
  );

  return (
    <FieldWrapper
      label="Select a contact"
      data-testid="contact-search-input-container"
    >
      <InputSelectionList
        value={selectedUser ? [selectedUser] : undefined}
        onChange={(e) => handleOnChange(e.selectedValues[0])}
        onToggleDropdown={handleToggleDropdown}
        getItemTitleByValue={(user) => (user ? getUserDisplayName(user) : null)}
        filter={{
          name: 'mergeContactSearchInput',
          value: searchQuery,
          onChange: (e) => setSearchQuery(e.target.value),
        }}
        dropdownProps={{
          className: styles.dropdown,
          style: {
            ...dropdownPositionOverride,
            display:
              searchQuery.length === 0 ||
              (searchUsers.length === 0 && asyncStatus !== 'pending')
                ? 'none'
                : undefined,
          },
        }}
        placeholder={'Search after name, phone number or email'}
      >
        {searchUsers.map((searchUser) => {
          const name = getUserDisplayName(searchUser);
          const secondLine = searchUser.email ?? searchUser.phone_number;
          const isNameAndSecondLineEqual = name === secondLine;

          return (
            <SelectionList.Item
              key={searchUser.id}
              value={searchUser}
              contentClassName={styles.itemContent}
              noFixedSelectionStateSpace
            >
              <Avatar
                user={{
                  name: searchUser.display_name,
                  avatar_url: searchUser.avatar_url,
                  phone_number: searchUser.phone_number,
                  email: searchUser.email,
                }}
              />

              <div>
                <Text.Heading variant="heading-4">{name}</Text.Heading>

                {!isNameAndSecondLineEqual && (
                  <Text.Paragraph variant="4" subdued>
                    <FlexBox gap="200" alignItems="center">
                      {searchUser.phone_number && (
                        <span>{searchUser.phone_number}</span>
                      )}

                      {searchUser.email && <span>{searchUser.email}</span>}
                    </FlexBox>
                  </Text.Paragraph>
                )}
              </div>
            </SelectionList.Item>
          );
        })}

        {searchQuery.length && asyncStatus === 'pending' && (
          <FlexBox
            alignItems="center"
            justifyContent="center"
            padding="400 200"
          >
            <Spinner size={24} />
          </FlexBox>
        )}

        {searchQuery.length && asyncStatus === 'fulfilled' && hasNextPage && (
          <Waypoint onEnter={nextPage} />
        )}
      </InputSelectionList>
    </FieldWrapper>
  );
};
